import {
  useRouter,
  useShoppingType,
  useUrlParameterFromRegex,
} from "@edreamsodigeo/frontend-core";

import { ShoppingType } from "~/generated/graphql";

const bookingIdRegex = new RegExp(/.*\/(\d{10,12})\/.*$/);
const uuidPatternRegex = new RegExp(
  /^([a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12})$/,
);
const basketIdRegex = new RegExp(
  /.*\/([a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12})\/.*$/,
);
const INVALID_SHOPPING_ID = 0;

export const useBookingIdFromUrl = (): number => {
  const { query, pathname } = useRouter();

  const matchingBookingId = pathname.match(bookingIdRegex);
  const bookingId = matchingBookingId
    ? matchingBookingId[1]
    : `${INVALID_SHOPPING_ID}`;
  const bookingIdFromQuery = query.bookingId;
  const queryBookingId = Array.isArray(bookingIdFromQuery)
    ? bookingIdFromQuery[0]
    : bookingIdFromQuery;

  return parseInt(
    queryBookingId && queryBookingId !== "" ? queryBookingId : bookingId,
    10,
  );
};

const BOOKING_ID_REGEX = /\/(\d{10,12})(?:\/|$)/;

const useShoppingIdFromUrl = (): string => {
  const { query } = useRouter();
  const [, bookingId] = useUrlParameterFromRegex(BOOKING_ID_REGEX);
  const shoppingIdIdParam = query?.bookingId;
  return (shoppingIdIdParam || bookingId || "0") as string;
};

const useBasketIdFromUrl = (): string => {
  const { query, pathname } = useRouter();

  const matchingBasketIds = pathname.match(basketIdRegex);
  const basketId = matchingBasketIds ? matchingBasketIds[1] : "";
  const shoppingIdFromQuery = query.shoppingId;
  const shoppingId = Array.isArray(shoppingIdFromQuery)
    ? shoppingIdFromQuery[0]
    : shoppingIdFromQuery;

  return shoppingId && shoppingId !== "" ? shoppingId : basketId;
};

const useShoppingInfoWithCart = (): ShoppingInfo => ({
  shoppingId: `${useShoppingIdFromUrl()}`,
  shoppingType: ShoppingType.Cart,
});

const useShoppingInfoWithBasket = (): ShoppingInfo => ({
  shoppingId: useBasketIdFromUrl(),
  shoppingType: ShoppingType.BasketV3,
});

interface ShoppingInfo {
  shoppingId: string;
  shoppingType: ShoppingType;
}
export const useShoppingInfo = (): ShoppingInfo => {
  const shoppingType = useShoppingType();
  const shoppingInfoWithShoppingCart = useShoppingInfoWithCart();
  const shoppingInfoWithShoppingBasket = useShoppingInfoWithBasket();
  return shoppingType === "CART"
    ? shoppingInfoWithShoppingCart
    : shoppingInfoWithShoppingBasket;
};

export const isValidShoppingId = (shoppingId: string | number): boolean =>
  isShoppingCartId(shoppingId)
    ? isValidShoppingCartId(shoppingId as number)
    : isValidShoppingBasketId(shoppingId as string);

const isValidShoppingCartId = (shoppingId: number): boolean =>
  shoppingId !== INVALID_SHOPPING_ID && shoppingId > 0;

const isValidShoppingBasketId = (shoppingId: string): boolean =>
  !!shoppingId && uuidPatternRegex.test(shoppingId);

const isShoppingCartId = (shoppingId: string | number): boolean =>
  typeof shoppingId === "number" || !isNaN(Number(shoppingId));
